import React, { useState } from "react";
import "../styles/ContactSerene.css";
import bgImage from "../assets/serene-images/contact-bg.webp";
import { motion, AnimatePresence } from "framer-motion";

export default function ContactSerene() {
  const [formData, setFormData] = useState({
    firstName: '',
    phone: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState({ submitting: false, success: false, error: null });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    const data = new FormData();
    Object.keys(formData).forEach(key => {
      data.append(key, formData[key]);
    });

    try {
      // Assuming the PHP script is hosted here, you can change the URL to your actual endpoint
      const response = await fetch("https://wowobysamathachowdary.com/php-backend/contact-serene.php", {
        method: "POST",
        body: data
      });

      const result = await response.json();
      if (result.status === "success") {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({ firstName: '', phone: '', email: '', message: '' }); // Reset form
      } else {
        setStatus({ submitting: false, success: false, error: result.message || "Submission failed" });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setStatus({ submitting: false, success: false, error: "Network error. Please try again later." });
    }
  };

  // ✅ Section + Card stagger
  const cardStagger = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.18,
        delayChildren: 0.1,
      },
    },
  };

  // ✅ Bottom → top
  const fadeUp = {
    hidden: { opacity: 0, y: 45, filter: "blur(10px)" },
    show: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.9, ease: "easeOut" },
    },
  };

  // ✅ Word by word title animation
  const wordContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.15,
      },
    },
  };

  const wordVariant = {
    hidden: { y: 35, opacity: 0, filter: "blur(6px)" },
    show: {
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  // ✅ Title split into 2 lines
  const line1 = "Begin Your Journey".split(" ");
  const line2 = "Today".split(" ");

  return (
    <section id="serene-contact" className="contact-serene">
      <AnimatePresence>
        {status.submitting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              zIndex: 9999,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div className="spinner-border text-white mb-3" style={{ width: "3.5rem", height: "3.5rem" }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h4 className="text-white fw-light m-0">Submitting your Message...</h4>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Background image */}
      <div
        className="contact-bg"
        style={{ backgroundImage: `url(${bgImage})` }}
      ></div>

      {/* Centered card */}
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-11">
            {/* ✅ Animated Contact Card */}
            <motion.div
              className="contact-card text-white rounded-3 p-4 p-md-5"
              variants={cardStagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: false, amount: 0.4 }}
            >
              <div className="row g-4 g-md-5">
                {/* LEFT */}
                <div className="col-lg-6">
                  {/* ✅ Title word-by-word */}
                  <motion.h2
                    className="contact-titles display-4 mb-4"
                    variants={wordContainer}
                  >
                    {line1.map((word, i) => (
                      <motion.span
                        key={`line1-${i}`}
                        variants={wordVariant}
                        style={{
                          display: "inline-block",
                          marginRight: "0.35em",
                        }}
                      >
                        {word}
                      </motion.span>
                    ))}
                    <br />
                    {line2.map((word, i) => (
                      <motion.span
                        key={`line2-${i}`}
                        variants={wordVariant}
                        style={{
                          display: "inline-block",
                          marginRight: "0.35em",
                        }}
                      >
                        {word}
                      </motion.span>
                    ))}
                  </motion.h2>

                  <motion.p className="contact-text" variants={fadeUp}>
                    Share your details and help us understand your requirements.
                    Explore thoughtfully crafted homes designed for refined living.
                    Receive personalized assistance from our experienced team.
                    We’ll get in touch to guide you every step of the way.
                  </motion.p>
                </div>

                {/* RIGHT */}
                <div className="col-lg-6">
                  <motion.form className="contact-form" variants={fadeUp} onSubmit={handleSubmit}>
                    {/* INPUTS */}
                    <motion.div className="mb-3" variants={fadeUp}>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        autoComplete="name"
                        className="form-control"
                        placeholder="Full Name"
                        required
                      />
                    </motion.div>

                    <motion.div className="mb-3" variants={fadeUp}>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        autoComplete="tel"
                        className="form-control"
                        placeholder="+91 (0000) 000-000"
                        required
                      />
                    </motion.div>

                    <motion.div className="mb-3" variants={fadeUp}>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        autoComplete="email"
                        className="form-control"
                        placeholder="Your email"
                        required
                      />
                    </motion.div>

                    {/* MESSAGE */}
                    <motion.div className="mb-4" variants={fadeUp}>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Your message"
                        rows="4"
                        required
                      />
                    </motion.div>

                    {status.success && <div className="alert alert-success mt-3 mb-3">Message sent successfully!</div>}
                    {status.error && <div className="alert alert-danger mt-3 mb-3">{status.error}</div>}

                    <motion.button
                      type="submit"
                      className="location-btn"
                      variants={fadeUp}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      disabled={status.submitting}
                    >
                      {status.submitting ? "Sending..." : "Send Message"}
                      <i className="fa-solid fa-arrow-right ms-2"></i>
                    </motion.button>
                  </motion.form>
                </div>
              </div>
            </motion.div>
            {/* ✅ end card */}
          </div>
        </div>
      </div>
    </section>
  );
}
